/**
 * LeftRail — persistent 280px rail for the /phone v2 workspace.
 * Same on ALL FOUR tabs (Inbox, Contacts, Text Blast, Fax).
 *
 * TOP: GoldStatusBar with three states (idle/in-call/queue-override).
 * BODY (scrolls): unified dial-and-search input, compact keypad,
 * queue card (waiting callers + park slots), speed dials.
 *
 * All in-call controls come from the shared <CallControls>
 * (components/phone/CallControls.jsx) — never re-implement here.
 */
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, PhoneCall, User2, Voicemail as VMIcon, MessageSquare, Search } from "lucide-react";
import { useCall } from "../../components/layout/ActiveCallOverlay";
import CallControls from "../../components/phone/CallControls";
import { formatPhoneNumber, cn } from "../../lib/utils";
import { NAVY, GOLD, GREEN, RED, NAVY_TINT, NAVY_TINT_STRG, NAVY_HAIRLINE } from "./tokens";

/* ═════════════════ Gold status bar (3 states) ═════════════════ */
function GoldStatusBar({ call, counts, queueCallers, onFilter, onPickupPark, parkSlots }) {
  const activeCall = call?.activeCall;
  const inCall = !!activeCall;
  const queueOverride = !inCall && queueCallers.length > 0;
  // Fixed height per PART 2 (SHELL): "ALWAYS VISIBLE at a FIXED HEIGHT
  // so nothing below shifts". 92px covers the tallest state.
  const [elapsed, setElapsed] = useState(0);
  useMemo(() => { /* placeholder for hooks parity */ }, []);

  // Timer — only tick when actually in a call. Tabular numerals for
  // no-shift readability.
  const timerRef = useRef(null);
  if (inCall && !timerRef.current) {
    timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  }
  if (!inCall && timerRef.current) {
    clearInterval(timerRef.current); timerRef.current = null;
    // reset visual state without re-render loop
    if (elapsed !== 0) setTimeout(() => setElapsed(0), 0);
  }
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  const longestHold = queueCallers.reduce((m, q) => {
    const w = Number(q.wait_seconds || q.waiting_seconds || 0);
    return w > m ? w : m;
  }, 0);
  const holdLabel = longestHold >= 60
    ? `${Math.floor(longestHold/60)}m${(longestHold%60).toString().padStart(2, "0")}`
    : `${longestHold}s`;

  return (
    <div
      className="shrink-0 border-b"
      style={{ background: GOLD, color: NAVY, borderColor: "rgba(20,56,117,0.15)", minHeight: 92 }}
      data-testid="comms2-gold-bar"
    >
      {inCall ? (
        <div className="px-3 py-2.5" data-testid="comms2-gold-bar-in-call">
          <div className="flex items-center gap-2 mb-1">
            {/* Red recording dot — visible for the ENTIRE call, not just at connect. */}
            <span
              className="w-2.5 h-2.5 rounded-full animate-pulse"
              style={{ background: RED }}
              aria-label="recording"
            />
            <span className="text-[13px] font-semibold truncate">
              {activeCall.contact?.name || activeCall.contact?.display_name || formatPhoneNumber(activeCall.from || activeCall.to) || "Unknown"}
            </span>
          </div>
          <div className="flex items-center justify-between text-[11.5px] mb-1.5">
            <span className="opacity-80">On call · recording</span>
            <span className="font-mono tabular-nums font-semibold">{mm}:{ss}</span>
          </div>
          {/* Shared in-call controls — no second implementation anywhere. */}
          <div className="mt-1"><CallControls variant="compact" /></div>
        </div>
      ) : queueOverride ? (
        <div className="px-3 py-2.5" data-testid="comms2-gold-bar-queue">
          <div className="flex items-center gap-2 mb-1">
            <User2 className="w-4 h-4" style={{ color: NAVY }} />
            <span className="text-[13px] font-semibold">Queue</span>
          </div>
          <div className="text-[12px] font-medium mb-1.5">
            {queueCallers.length} caller{queueCallers.length === 1 ? "" : "s"} waiting · longest {holdLabel}
          </div>
          <button
            onClick={() => onFilter && onFilter("calls")}
            className="w-full h-8 rounded-md text-[12px] font-semibold border-2 hover:brightness-95 transition bg-transparent"
            style={{ borderColor: NAVY, color: NAVY }}
            data-testid="comms2-queue-view"
          >
            View queue
          </button>
        </div>
      ) : (
        <div className="px-3 py-2.5" data-testid="comms2-gold-bar-idle">
          <div className="flex items-center gap-2 mb-1.5">
            <PhoneCall className="w-4 h-4" style={{ color: NAVY }} />
            <span className="text-[13px] font-semibold">Line ready</span>
            <span className="ml-auto text-[11px] opacity-70">no active call</span>
          </div>
          {/* Three tappable counts. Colour is #FFCC1A tint of navy so they
              read as ONE unit with the gold band (see PART 1). No timer,
              no rec dot — those live only in the in-call state. */}
          <div className="grid grid-cols-3 gap-1.5">
            <CountChip label="to call back" n={counts.toCallBack} onClick={() => onFilter && onFilter("calls")} testid="comms2-count-callback" />
            <CountChip label="voicemail"    n={counts.vm}         onClick={() => onFilter && onFilter("vm")}    testid="comms2-count-vm" />
            <CountChip label="unread"       n={counts.unread}     onClick={() => onFilter && onFilter("texts")} testid="comms2-count-unread" />
          </div>
        </div>
      )}
    </div>
  );
}

function CountChip({ label, n, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="rounded-md py-1.5 text-center transition-colors hover:brightness-95"
      style={{ background: NAVY_TINT_STRG, color: NAVY }}
      data-testid={testid}
    >
      <div className="text-[18px] font-bold leading-tight tabular-nums">{n}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">{label}</div>
    </button>
  );
}

/* ═════════════════ Dial + search input ═════════════════ */
function DialAndSearch({ value, setValue, onDial, contactsIndex, fromNumber }) {
  const q = value || "";
  const setQ = setValue;
  const digitsOnly = q.replace(/\D/g, "");
  const looksLikeNumber = digitsOnly.length >= 3 && digitsOnly.length === q.replace(/[\s()+\-.]/g, "").length;
  const results = useMemo(() => {
    if (!q.trim() || looksLikeNumber) return [];
    const term = q.trim().toLowerCase();
    return Object.values(contactsIndex || {})
      .filter(c => {
        const name = `${c.first_name || ""} ${c.last_name || ""}`.trim().toLowerCase();
        return name.includes(term);
      })
      .slice(0, 6);
  }, [q, contactsIndex, looksLikeNumber]);

  const submit = () => {
    if (looksLikeNumber && digitsOnly.length >= 7) {
      onDial && onDial(q, fromNumber);
      setQ("");
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 rounded-lg border" style={{ borderColor: NAVY_HAIRLINE, background: "white" }}>
        <Search className="w-4 h-4 ml-2.5 opacity-60" style={{ color: NAVY }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Name or number"
          className="flex-1 h-11 text-[14px] bg-transparent outline-none"
          style={{ color: NAVY }}
          data-testid="comms2-dial-search"
        />
        {q && (
          <button
            onClick={() => setQ("")}
            className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center shrink-0"
            title="Clear"
            data-testid="comms2-dial-clear"
          >
            <span className="text-[13px] text-slate-400">✕</span>
          </button>
        )}
        {looksLikeNumber && digitsOnly.length >= 7 && (
          <button
            onClick={submit}
            className="h-9 mr-1 px-3 rounded-md text-white text-[12px] font-semibold flex items-center shrink-0"
            style={{ background: GREEN }}
            data-testid="comms2-dial-submit"
            title="Call"
          >
            <Phone className="w-4 h-4" />
          </button>
        )}
      </div>
      {results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-30 max-h-64 overflow-y-auto" style={{ borderColor: NAVY_HAIRLINE }}>
          {results.map(c => (
            <button
              key={c.id}
              onClick={() => { onDial && onDial(c.phone_number, fromNumber); setQ(""); }}
              className="w-full flex items-center gap-2 px-2.5 py-2 hover:bg-slate-50 text-left"
              data-testid={`comms2-dial-suggest-${c.id}`}
            >
              <span className="text-[13px] font-medium truncate flex-1" style={{ color: NAVY }}>
                {c.first_name} {c.last_name}
              </span>
              <span className="text-[11px] font-mono opacity-70">{formatPhoneNumber(c.phone_number)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ═════════════════ Keypad ═════════════════ */
function Keypad({ onKey }) {
  const KEYS = [
    { d: "1", sub: "" },    { d: "2", sub: "ABC" }, { d: "3", sub: "DEF" },
    { d: "4", sub: "GHI" }, { d: "5", sub: "JKL" }, { d: "6", sub: "MNO" },
    { d: "7", sub: "PQRS" },{ d: "8", sub: "TUV" }, { d: "9", sub: "WXYZ" },
    { d: "*", sub: "" },    { d: "0", sub: "+" },   { d: "#", sub: "" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="comms2-keypad">
      {KEYS.map(({ d, sub }) => (
        <button
          key={d}
          onClick={() => onKey && onKey(d)}
          className="h-12 rounded-xl flex flex-col items-center justify-center hover:brightness-95 active:scale-[0.97] transition"
          style={{ background: NAVY_TINT, color: NAVY }}
          data-testid={`comms2-key-${d}`}
        >
          <span className="text-[18px] font-semibold leading-none">{d}</span>
          {sub && <span className="text-[8px] font-semibold tracking-widest text-slate-400 mt-0.5">{sub}</span>}
        </button>
      ))}
    </div>
  );
}

/* ═════════════════ Queue + Park card ═════════════════ */
function QueueCard({ queueCallers, parkSlots, onPickupPark, call, fromNumber }) {
  const active = !!call?.activeCall;
  return (
    <div className="rounded-lg border" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-queue-card">
      <div className="px-2.5 py-1.5 border-b flex items-center justify-between" style={{ borderColor: NAVY_HAIRLINE }}>
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: NAVY }}>Queue</div>
        <div className="text-[11px] opacity-70">{queueCallers.length} waiting</div>
      </div>
      <div className="p-2 space-y-1">
        {queueCallers.length === 0 && (
          <div className="text-[11.5px] text-slate-500 italic px-1">No callers waiting</div>
        )}
        {queueCallers.slice(0, 3).map((q, i) => (
          <div key={q.id || i} className="flex items-center gap-2 rounded-md px-2 py-1" style={{ background: NAVY_TINT }}>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-medium truncate" style={{ color: NAVY }}>
                {q.contact_name || formatPhoneNumber(q.from_number) || "Unknown"}
              </div>
              <div className="text-[10.5px] text-slate-500 tabular-nums">{fmtDur(q.wait_seconds || q.waiting_seconds || 0)}</div>
            </div>
            <button
              className="h-6 px-2 rounded text-white text-[10.5px] font-semibold disabled:opacity-40"
              style={{ background: GREEN }}
              disabled={active}
              data-testid={`comms2-queue-answer-${q.id || i}`}
            >
              Answer
            </button>
          </div>
        ))}
        {parkSlots.length > 0 && (
          <>
            <div className="text-[10px] uppercase font-semibold tracking-wider pt-1" style={{ color: NAVY, opacity: 0.7 }}>Parked</div>
            {parkSlots.slice(0, 3).map(p => (
              <button
                key={p.slot || p.id}
                onClick={() => onPickupPark && onPickupPark(p.slot)}
                className="w-full flex items-center justify-between rounded px-2 py-1 text-[11.5px] hover:brightness-95"
                style={{ background: NAVY_TINT }}
                data-testid={`comms2-park-${p.slot || p.id}`}
              >
                <span className="truncate font-medium" style={{ color: NAVY }}>
                  Park {p.slot} · {p.contact_name || formatPhoneNumber(p.from_number) || "Unknown"}
                </span>
                <span className="text-[10px] opacity-70 tabular-nums">{fmtDur(p.parked_seconds || 0)}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function fmtDur(s) {
  const n = Math.max(0, Math.floor(s || 0));
  if (n < 60) return `${n}s`;
  return `${Math.floor(n/60)}m${(n%60).toString().padStart(2, "0")}`;
}

/* ═════════════════ Speed dials — 3 across ═════════════════ */
function SpeedDials({ speedDials, onDial, fromNumber }) {
  const items = (speedDials || []).slice(0, 6);
  return (
    <div className="rounded-lg border" style={{ borderColor: NAVY_HAIRLINE }} data-testid="comms2-speed-dials">
      <div className="px-2.5 py-1.5 border-b" style={{ borderColor: NAVY_HAIRLINE }}>
        <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: NAVY }}>Speed dial</div>
      </div>
      <div className="p-2 grid grid-cols-3 gap-1.5">
        {items.map(sd => (
          <button
            key={sd.id}
            onClick={() => onDial && onDial(sd.phone_number, fromNumber)}
            className="rounded-md py-2 px-1.5 text-center transition-colors hover:brightness-95 active:scale-[0.97]"
            style={{ background: NAVY_TINT, color: NAVY }}
            title={sd.phone_number}
            data-testid={`comms2-speed-dial-${sd.id}`}
          >
            <div className="text-[11.5px] font-semibold truncate">{sd.name}</div>
          </button>
        ))}
        {/* Only "+ Add" — no dashed em-dash placeholders (bug #7). */}
        <Link
          to="/admin/phone-lines?tab=speed-dials"
          className="rounded-md py-2 border-dashed border text-center text-[11px] font-medium hover:brightness-95"
          style={{ borderColor: "rgba(20,56,117,0.3)", color: NAVY }}
          data-testid="comms2-speed-dial-add"
        >
          + Add
        </Link>
      </div>
    </div>
  );
}

export default function LeftRail({
  counts,
  queueCallers,
  parkSlots,
  speedDials,
  contactsIndex,
  fromNumber,
  onFilter,
  onPickupPark,
  dialInputRef,
}) {
  const call = useCall() || {};
  const dial = (num, from) => call?.makeCall && call.makeCall(num, from);
  const [dialValue, setDialValue] = useState("");

  return (
    <aside
      className="hidden lg:flex flex-col overflow-hidden bg-white rounded-xl shadow-sm border w-[300px] shrink-0"
      style={{ borderColor: NAVY_HAIRLINE }}
      data-testid="comms2-left-rail"
    >
      <GoldStatusBar
        call={call}
        counts={counts}
        queueCallers={queueCallers}
        parkSlots={parkSlots}
        onFilter={onFilter}
        onPickupPark={onPickupPark}
      />
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <DialAndSearch
          value={dialValue}
          setValue={setDialValue}
          onDial={dial}
          contactsIndex={contactsIndex}
          fromNumber={fromNumber}
        />
        <Keypad onKey={(k) => setDialValue(v => (v + k).slice(0, 20))} />
        <QueueCard
          queueCallers={queueCallers}
          parkSlots={parkSlots}
          onPickupPark={onPickupPark}
          call={call}
          fromNumber={fromNumber}
        />
        <SpeedDials
          speedDials={speedDials}
          onDial={dial}
          fromNumber={fromNumber}
        />
      </div>
    </aside>
  );
}
